'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/features/auth/hooks'
import { useApplyReissuedTokens } from '@/features/auth/hooks/use-apply-reissued-tokens'
import {
  listSessions,
  logout,
  mintMessagingLinkNonce,
  revokeAllOtherSessions,
  revokeSession,
} from '@/service/session.api'
import {
  LogoutResponse,
  RevokeAllOtherSessionsResponse,
  RevokeSessionResponse,
} from '@/types/generated/common/auth'
import { MintMessagingLinkNonceResponse } from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

// Query keys
export const sessionKeys = {
  all: (userId?: string) => ['sessions', userId] as const,
  list: (userId?: string) => [...sessionKeys.all(userId), 'list'] as const,
}

export const useListSessions = () => {
  const { user, accessToken } = useAuth()
  return useQuery({
    queryKey: sessionKeys.list(user?.userId),
    queryFn: listSessions,
    // Session list changes on every login/refresh elsewhere — keep it fresher
    // than the 5-minute passkey staleTime.
    staleTime: 30 * 1000,
    enabled: Boolean(user?.emailVerified) && Boolean(accessToken),
    // A Telegram link completes entirely outside this tab (the bot replies in
    // the chat, nothing pushes to the browser) — the only signal the client
    // gets that a hand-off finished is the user returning focus to this tab.
    // 'always' forces a refetch on every focus regardless of staleTime, so
    // that return trip reliably surfaces the new session instead of waiting
    // out the 30s window.
    refetchOnWindowFocus: 'always',
  })
}

export const useRevokeSession = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: RevokeSessionResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: revokeSession,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.list(user?.userId) })
      onSuccess?.(response)
    },
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

export const useRevokeAllOtherSessions = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: RevokeAllOtherSessionsResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const applyReissuedTokens = useApplyReissuedTokens()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: revokeAllOtherSessions,
    onSuccess: async (response) => {
      // Nuke-and-reissue: adopt the fresh tokens before the invalidation below
      // refetches the list, or that refetch runs on a just-revoked token.
      await applyReissuedTokens(response)
      queryClient.invalidateQueries({ queryKey: sessionKeys.list(user?.userId) })
      onSuccess?.(response)
    },
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

export const useLogout = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: LogoutResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: logout,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

// Deliberately does NOT invalidate sessionKeys.list on success, unlike
// useRevokeSession — minting a nonce doesn't create a session by itself, the
// user still has to complete the hand-off in Telegram outside this app.
// useListSessions' own refetchOnWindowFocus: 'always' is what surfaces the
// new session once they return to this tab.
export const useMintMessagingLinkNonce = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: MintMessagingLinkNonceResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: mintMessagingLinkNonce,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}
